import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsService, auditorsService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import {
  ArrowLeft, Search, Download, FileText, Loader2,
  Building2, MapPin, Phone, User, Calendar,
  Filter, Columns3, AlertTriangle, CheckCircle2,
  X, Users, ChevronDown, Layers
} from 'lucide-react';
import { format } from 'date-fns';

// Flag definitions with labels and icons
const FLAG_DEFS = [
  { key: 'flag_has_schedule_contact', label: 'Has schedule notification contact', group: 'contacts' },
  { key: 'flag_has_report_contact', label: 'Has report sending contact', group: 'contacts' },
  { key: 'flag_missing_telephone', label: 'Missing telephone', group: 'data' },
  { key: 'flag_missing_contact', label: 'Missing contact person', group: 'data' },
  { key: 'flag_missing_address', label: 'Missing address', group: 'data' },
  { key: 'flag_missing_year_end', label: 'Missing year-end date', group: 'data' },
  { key: 'flag_never_audited', label: 'Never audited', group: 'audit' },
  { key: 'flag_no_next_audit', label: 'No next audit date', group: 'audit' },
  { key: 'flag_recently_audited', label: 'Recently audited (30d)', group: 'audit' },
  { key: 'flag_multi_contact', label: 'Multi-contact site', group: 'contacts' },
  { key: 'flag_inactive', label: 'Inactive / archived', group: 'status' },
  { key: 'flag_has_pricing', label: 'Has pricing info', group: 'pricing', adminOnly: true },
];

const OPTIONAL_COLS = [
  // Core contact/location fields (visible by default; user can hide)
  { key: 'include_address', label: 'Address', defaultOn: true },
  { key: 'include_telephone', label: 'Telephone', defaultOn: true },
  { key: 'include_contacts', label: 'Contact(s)', defaultOn: true },
  { key: 'include_year_end', label: 'Year End', defaultOn: true },
  // Optional audit/scheduling columns
  { key: 'include_last_audit', label: 'Last Audit Date', defaultOn: true },
  { key: 'include_next_audit', label: 'Next Audit Date', defaultOn: true },
  { key: 'include_auditors', label: 'Auditor(s)', defaultOn: true },
  { key: 'include_sales', label: 'Sales', defaultOn: false, adminOnly: true },
  { key: 'include_pricing_lines', label: 'Default Pricing Lines', defaultOn: false, adminOnly: true },
];

export default function CustomerSiteDirectoryPage() {
  const { toast } = useToast();
  const { isAdmin, isScheduler, isAuditor } = useAuth();
  const { region, currencySymbol } = useSettings();
  const navigate = useNavigate();
  const isAuditorOnly = isAuditor && !isAdmin && !isScheduler;

  // State
  const [loading, setLoading] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Auditor filter
  const [auditors, setAuditors] = useState([]);
  const [selectedAuditorId, setSelectedAuditorId] = useState('');
  const [auditorSearch, setAuditorSearch] = useState('');

  useEffect(() => {
    if (!isAuditorOnly) {
      auditorsService.list().then(list => {
        const sorted = (Array.isArray(list) ? list : list?.items || [])
          .filter(a => a.first_name && a.last_name)
          .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
        setAuditors(sorted);
      }).catch(() => {});
    }
  }, [isAuditorOnly]);

  const filteredAuditors = useMemo(() => {
    if (!auditorSearch.trim()) return auditors;
    const s = auditorSearch.toLowerCase();
    return auditors.filter(a => `${a.first_name} ${a.last_name}`.toLowerCase().includes(s));
  }, [auditors, auditorSearch]);

  const selectedAuditorName = useMemo(() => {
    if (!selectedAuditorId) return '';
    const a = auditors.find(a => String(a.id) === String(selectedAuditorId));
    return a ? `${a.first_name} ${a.last_name}` : '';
  }, [selectedAuditorId, auditors]);

  // Optional columns
  const [columns, setColumns] = useState({
    include_address: true,
    include_telephone: true,
    include_contacts: true,
    include_year_end: true,
    include_last_audit: true,
    include_next_audit: true,
    include_auditors: true,
    include_sales: false,
    include_pricing_lines: false,
  });
  // Sales date range
  const today = new Date();
  const [salesStart, setSalesStart] = useState(format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd'));
  const [salesEnd, setSalesEnd] = useState(format(today, 'yyyy-MM-dd'));

  // Flags — null = not filtering, true = only matching, false = only non-matching
  const [flags, setFlags] = useState({});
  const activeFlags = useMemo(() => Object.entries(flags).filter(([, v]) => v !== undefined && v !== null), [flags]);

  // Build params
  const buildParams = useCallback(() => {
    const p = {
      include_inactive: includeInactive,
      include_address: columns.include_address,
      include_telephone: columns.include_telephone,
      include_contacts: columns.include_contacts,
      include_year_end: columns.include_year_end,
      include_last_audit: columns.include_last_audit,
      include_next_audit: columns.include_next_audit,
      include_auditors: columns.include_auditors,
      include_sales: columns.include_sales && !isAuditorOnly,
      include_pricing_lines: columns.include_pricing_lines && !isAuditorOnly,
      region: region || undefined,
    };
    if (search.trim()) p.search = search.trim();
    if (columns.include_sales && !isAuditorOnly && salesStart) p.sales_start = salesStart;
    if (columns.include_sales && !isAuditorOnly && salesEnd) p.sales_end = salesEnd;
    if (selectedAuditorId) p.auditor_id = selectedAuditorId;
    // flags
    for (const [k, v] of Object.entries(flags)) {
      if (v !== null && v !== undefined) p[k] = v;
    }
    return p;
  }, [search, includeInactive, columns, salesStart, salesEnd, flags, region, isAuditorOnly, selectedAuditorId]);

  // Load
  const generateReport = useCallback(async () => {
    setLoading(true);
    try {
      const result = await reportsService.getCustomerSiteDirectory(buildParams());
      setData(result);
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed to load directory', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [buildParams, toast]);

  // Export CSV
  const handleExportCSV = useCallback(async () => {
    setExportingCsv(true);
    try {
      const blob = await reportsService.exportCustomerSiteDirectoryCSV(buildParams());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customer_site_directory_${format(today, 'yyyy-MM-dd')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Export complete' });
    } catch {
      toast({ title: 'Export failed', variant: 'destructive' });
    } finally {
      setExportingCsv(false);
    }
  }, [buildParams, toast, today]);

  // Export PDF
  const handleExportPDF = useCallback(async () => {
    setExportingPdf(true);
    try {
      const blob = await reportsService.exportCustomerSiteDirectoryPDF(buildParams());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customer_site_directory_${format(today, 'yyyy-MM-dd')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'PDF exported' });
    } catch {
      toast({ title: 'PDF export failed', variant: 'destructive' });
    } finally {
      setExportingPdf(false);
    }
  }, [buildParams, toast, today]);

  const toggleFlag = (key, value) => {
    setFlags(prev => ({ ...prev, [key]: prev[key] === value ? null : value }));
  };

  const clearAllFlags = () => setFlags({});

  const visibleFlags = FLAG_DEFS.filter(f => !f.adminOnly || !isAuditorOnly);
  const visibleCols = OPTIONAL_COLS.filter(c => !c.adminOnly || !isAuditorOnly);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-site-directory-page">
      <div className="max-w-[1400px] mx-auto px-4 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/reports')} data-testid="back-to-reports">
              <ArrowLeft className="h-4 w-4 mr-1" /> Reports
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-800" data-testid="directory-title">Customer & Site Directory</h1>
              <p className="text-sm text-muted-foreground">Searchable directory of all customers and sites</p>
            </div>
          </div>
          {data && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={exportingCsv} data-testid="export-csv-btn">
                {exportingCsv ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />} CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={exportingPdf} data-testid="export-pdf-btn">
                {exportingPdf ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />} PDF
              </Button>
            </div>
          )}
        </div>

        {/* Controls Card */}
        <Card>
          <CardContent className="pt-5 space-y-4">
            {/* Search + Generate */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by customer, site, postcode, city, phone, contact..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="directory-search"
                  onKeyDown={e => e.key === 'Enter' && generateReport()}
                />
              </div>
              <Button onClick={generateReport} disabled={loading} data-testid="generate-report-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
                Generate
              </Button>
            </div>

            {/* Column picker + Flags row */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Column picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="column-picker-btn">
                    <Columns3 className="h-4 w-4 mr-1" /> Columns <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3 space-y-3" align="start">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Optional Columns</p>
                  {visibleCols.map(col => (
                    <div key={col.key} className="flex items-center justify-between">
                      <Label className="text-sm">{col.label}</Label>
                      <Switch
                        checked={columns[col.key]}
                        onCheckedChange={v => setColumns(prev => ({ ...prev, [col.key]: v }))}
                        data-testid={`col-toggle-${col.key}`}
                      />
                    </div>
                  ))}
                  {columns.include_sales && !isAuditorOnly && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <Label className="text-xs">Sales Date Range</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input type="date" value={salesStart} onChange={e => setSalesStart(e.target.value)} className="h-8 text-xs" data-testid="sales-start" />
                          <Input type="date" value={salesEnd} onChange={e => setSalesEnd(e.target.value)} className="h-8 text-xs" data-testid="sales-end" />
                        </div>
                      </div>
                    </>
                  )}
                </PopoverContent>
              </Popover>

              {/* Flags picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="flags-picker-btn">
                    <Filter className="h-4 w-4 mr-1" /> Flags
                    {activeFlags.length > 0 && <Badge variant="secondary" className="ml-1 h-5 px-1.5">{activeFlags.length}</Badge>}
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-3 space-y-2 max-h-[400px] overflow-y-auto" align="start">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase">Quick Flags</p>
                    {activeFlags.length > 0 && (
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={clearAllFlags}>Clear all</Button>
                    )}
                  </div>
                  {visibleFlags.map(f => {
                    const val = flags[f.key];
                    return (
                      <div key={f.key} className="flex items-center justify-between gap-2 py-1">
                        <span className="text-sm">{f.label}</span>
                        <div className="flex gap-1">
                          <Button
                            variant={val === true ? 'default' : 'outline'}
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => toggleFlag(f.key, true)}
                            data-testid={`flag-${f.key}-yes`}
                          >Yes</Button>
                          <Button
                            variant={val === false ? 'default' : 'outline'}
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => toggleFlag(f.key, false)}
                            data-testid={`flag-${f.key}-no`}
                          >No</Button>
                        </div>
                      </div>
                    );
                  })}
                </PopoverContent>
              </Popover>

              {/* Auditor filter - hidden from auditor-only users */}
              {!isAuditorOnly && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="auditor-filter-btn" className={selectedAuditorId ? 'border-primary' : ''}>
                      <Users className="h-4 w-4 mr-1" />
                      {selectedAuditorName || 'Auditor'}
                      <ChevronDown className="h-3 w-3 ml-1" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2" align="start">
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                          placeholder="Search auditors..."
                          value={auditorSearch}
                          onChange={e => setAuditorSearch(e.target.value)}
                          className="h-8 pl-7 text-sm"
                          data-testid="auditor-search-input"
                        />
                      </div>
                      <div className="max-h-[200px] overflow-y-auto space-y-0.5">
                        <button
                          className={`w-full text-left text-sm px-2 py-1.5 rounded hover:bg-slate-100 ${!selectedAuditorId ? 'bg-slate-100 font-medium' : ''}`}
                          onClick={() => { setSelectedAuditorId(''); setAuditorSearch(''); }}
                          data-testid="auditor-option-all"
                        >All Auditors</button>
                        {filteredAuditors.map(a => (
                          <button
                            key={a.id}
                            className={`w-full text-left text-sm px-2 py-1.5 rounded hover:bg-slate-100 ${String(selectedAuditorId) === String(a.id) ? 'bg-slate-100 font-medium' : ''}`}
                            onClick={() => { setSelectedAuditorId(a.id); setAuditorSearch(''); }}
                            data-testid={`auditor-option-${a.id}`}
                          >{a.first_name} {a.last_name}</button>
                        ))}
                        {filteredAuditors.length === 0 && (
                          <p className="text-xs text-muted-foreground text-center py-2">No auditors found</p>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              {/* Include inactive toggle */}
              <div className="flex items-center gap-2 ml-auto">
                <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} id="inc-inactive" data-testid="include-inactive" />
                <Label htmlFor="inc-inactive" className="text-sm cursor-pointer">Include inactive</Label>
              </div>
            </div>

            {/* Active flag badges */}
            {activeFlags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {activeFlags.map(([key, val]) => {
                  const def = FLAG_DEFS.find(f => f.key === key);
                  return (
                    <Badge key={key} variant="secondary" className="gap-1 pr-1">
                      {def?.label}: {val ? 'Yes' : 'No'}
                      <button onClick={() => setFlags(p => ({ ...p, [key]: null }))} className="ml-0.5 hover:bg-slate-300 rounded-full p-0.5">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary tiles */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="summary-tiles">
            <SummaryTile icon={Building2} label="Total Sites" value={data.summary.total_sites} />
            <SummaryTile icon={CheckCircle2} label="Active" value={data.summary.active_sites} color="text-green-600" />
            <SummaryTile icon={AlertTriangle} label="Never Audited" value={data.summary.sites_never_audited} color="text-amber-600" />
            <SummaryTile icon={Phone} label="Missing Phone" value={data.summary.sites_missing_telephone} color="text-red-500" />
            <SummaryTile icon={User} label="Missing Contact" value={data.summary.sites_missing_contact} color="text-red-500" />
          </div>
        )}

        {/* Results table */}
        {data && (
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                {data.rows.length} site{data.rows.length !== 1 ? 's' : ''} found
                {data.is_auditor_view && <Badge variant="outline" className="ml-2 text-xs">Your assigned sites only</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table data-testid="directory-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-xs">Customer / Group</TableHead>
                    <TableHead className="font-semibold text-xs">Site Name</TableHead>
                    {columns.include_address && <TableHead className="font-semibold text-xs">Address</TableHead>}
                    {columns.include_telephone && <TableHead className="font-semibold text-xs">Telephone</TableHead>}
                    {columns.include_contacts && <TableHead className="font-semibold text-xs">Contact(s)</TableHead>}
                    {columns.include_year_end && <TableHead className="font-semibold text-xs">Year End</TableHead>}
                    {columns.include_last_audit && <TableHead className="font-semibold text-xs">Last Audit</TableHead>}
                    {columns.include_next_audit && <TableHead className="font-semibold text-xs">Next Audit</TableHead>}
                    {columns.include_auditors && <TableHead className="font-semibold text-xs">Prev Auditor(s)</TableHead>}
                    {columns.include_auditors && <TableHead className="font-semibold text-xs">Next Auditor(s)</TableHead>}
                    {columns.include_sales && !isAuditorOnly && <TableHead className="font-semibold text-xs text-right">Sales</TableHead>}
                    {columns.include_pricing_lines && !isAuditorOnly && <TableHead className="font-semibold text-xs">Default Pricing</TableHead>}
                    <TableHead className="font-semibold text-xs text-center">Flags</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={99} className="text-center py-10 text-muted-foreground">No sites match your criteria</TableCell></TableRow>
                  ) : data.rows.map(row => (
                    <TableRow key={row.site_id} className={!row.is_active ? 'opacity-50' : ''} data-testid={`directory-row-${row.site_id}`}>
                      <TableCell className="text-sm font-medium">
                        {row.customer_name}
                        {row.is_group && <Badge variant="outline" className="ml-1.5 text-[10px] px-1 py-0">Group</Badge>}
                        {row.is_group && row.customer_id && (
                          <Button variant="ghost" size="sm" className="ml-1 h-5 text-[10px] px-1.5 text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                            onClick={(e) => { e.stopPropagation(); navigate(`/customers/${row.customer_id}/sub-regions`); }}
                            data-testid={`sub-regions-btn-${row.customer_id}`}>
                            <Layers className="h-3 w-3 mr-0.5" />Sub-Regions
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{row.site_name}</TableCell>
                      {columns.include_address && (
                        <TableCell className="text-xs text-muted-foreground max-w-[200px]">
                          {[row.address_line_1, row.city, row.postcode].filter(Boolean).join(', ') || '—'}
                        </TableCell>
                      )}
                      {columns.include_telephone && (
                        <TableCell className="text-sm">{row.telephone || <span className="text-muted-foreground">—</span>}</TableCell>
                      )}
                      {columns.include_contacts && (
                        <TableCell className="text-xs max-w-[180px]">
                          {row.contacts?.length > 0 ? (
                            <div className="space-y-0.5">
                              {row.contacts.slice(0, 2).map((c, i) => (
                                <div key={i} className="flex items-center gap-1">
                                  <span>{c.name}</span>
                                  {c.for_job_scheduling && <Badge variant="outline" className="text-[9px] px-1 py-0 bg-blue-50">Sched</Badge>}
                                  {c.for_job_reports && <Badge variant="outline" className="text-[9px] px-1 py-0 bg-green-50">Report</Badge>}
                                </div>
                              ))}
                              {row.contacts.length > 2 && <span className="text-[10px] text-muted-foreground">+{row.contacts.length - 2} more</span>}
                            </div>
                          ) : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                      )}
                      {columns.include_year_end && (
                        <TableCell className="text-sm font-medium" data-testid={`year-end-${row.site_id}`}>
                          {row.year_end_display || <span className="text-muted-foreground">—</span>}
                        </TableCell>
                      )}
                      {columns.include_last_audit && (
                        <TableCell className="text-sm">{row.last_audit_date ? format(new Date(row.last_audit_date), 'dd MMM yyyy') : <span className="text-muted-foreground">—</span>}</TableCell>
                      )}
                      {columns.include_next_audit && (
                        <TableCell className="text-sm">{row.next_audit_date ? format(new Date(row.next_audit_date), 'dd MMM yyyy') : <span className="text-muted-foreground">—</span>}</TableCell>
                      )}
                      {columns.include_auditors && (
                        <TableCell className="text-xs">{row.last_auditors?.length > 0 ? row.last_auditors.join(', ') : <span className="text-muted-foreground">—</span>}</TableCell>
                      )}
                      {columns.include_auditors && (
                        <TableCell className="text-xs">{row.next_auditors?.length > 0 ? row.next_auditors.join(', ') : <span className="text-muted-foreground">—</span>}</TableCell>
                      )}
                      {columns.include_sales && !isAuditorOnly && (
                        <TableCell className="text-sm text-right">{row.total_sales != null ? `${currencySymbol}${row.total_sales.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}</TableCell>
                      )}
                      {columns.include_pricing_lines && !isAuditorOnly && (
                        <TableCell className="text-xs max-w-[220px]">
                          {row.pricing_lines?.length > 0 ? (
                            <div className="space-y-0.5">
                              {row.pricing_lines.map((pl, i) => (
                                <div key={i} className="flex items-center gap-1">
                                  <span className="text-slate-600">{pl.service_name}:</span>
                                  <span className="font-medium">{currencySymbol}{pl.price.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          ) : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                      )}
                      <TableCell className="text-center">
                        <div className="flex flex-wrap justify-center gap-1">
                          {!row.has_telephone && <FlagBadge color="red" label="No Phone" />}
                          {!row.has_contact_person && <FlagBadge color="red" label="No Contact" />}
                          {!row.has_address && <FlagBadge color="amber" label="No Address" />}
                          {!row.year_end_display && <FlagBadge color="amber" label="No Year End" />}
                          {row.never_audited && <FlagBadge color="amber" label="Never Audited" />}
                          {row.recently_audited && <FlagBadge color="green" label="Recent" />}
                          {!row.is_active && <FlagBadge color="slate" label="Inactive" />}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {!data && !loading && (
          <Card className="py-16 text-center">
            <CardContent>
              <Building2 className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">Configure filters above and click <strong>Generate</strong> to view the directory.</p>
            </CardContent>
          </Card>
        )}
      </div>
      <Toaster />
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, color = 'text-slate-800' }) {
  return (
    <Card className="p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${color}`} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
    </Card>
  );
}

function FlagBadge({ color, label }) {
  const colors = {
    red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700',
    green: 'bg-green-100 text-green-700',
    slate: 'bg-slate-100 text-slate-600',
  };
  return <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${colors[color] || colors.slate}`}>{label}</span>;
}
